import type { SVGProps } from 'react'

import { DIRECTIONAL, ICON_PATHS, type IconName } from './iconPaths'

export type { IconName } from './iconPaths'

/** The three sizes the product draws a glyph at. `md` is the default. */
export type IconSize = 'sm' | 'md' | 'lg'

const SIZE_CLASS: Record<IconSize, string> = {
  sm: 'icon icon--sm',
  md: 'icon',
  lg: 'icon icon--lg',
}

/**
 * THE PRODUCT'S ONE ICON. Every glyph on every surface comes through here.
 *
 * WHY ICONS AT ALL. A registry header carries two controls per column — filter
 * and collapse — beside a heading that is itself the information. Spelled as
 * words they double the width of every heading and the eye reads six labels
 * where it wanted six column names; as glyphs they read as chrome and the
 * heading stays the loudest thing in the row. The same argument holds down the
 * actions column, where a worded control is one word written down every row.
 *
 * PURELY DECORATIVE BY CONSTRUCTION — `aria-hidden`, `focusable="false"`, and
 * neither is a prop a caller can turn off: they are omitted from the props type
 * AND written after the caller's spread, so neither a new call site nor a cast
 * can win. Every one of these lives inside a control that carries the
 * accessible name, so an icon that announced itself would make the control say
 * its purpose twice; and an icon that COULD become the sole label is an icon
 * that becomes unlabelled in the locale nobody develops in.
 *
 * THE GEOMETRY IS THE REGISTRY'S FOR THE SAME REASON. `fill`, `stroke`,
 * `strokeWidth` and `viewBox` are not the caller's either — a surface that set
 * its own stroke would be the fourth weight the three deleted icon components
 * used to draw at, arrived at one call site at a time.
 *
 * SIZE AND WEIGHT COME FROM THE STYLESHEET, not from this element. `size`
 * picks one of three token steps; the stroke is `--icon-stroke`. Three sets
 * previously drew at 1.6, 1.7 and whatever their container's `svg` rule said,
 * and the difference is visible where two of them meet in one row.
 *
 * DIRECTION IS THE DRAWING'S, not the caller's. A glyph listed in
 * `DIRECTIONAL` gains the class that `styles/icon.css` mirrors under `rtl`;
 * every other glyph is a picture and stays put.
 */
/**
 * Everything the drawing decides, rather than the surface that renders it. One
 * list, read twice: it removes these from the props a call site may write, and
 * it removes them again at runtime from whatever actually arrives, because a
 * type is not a guard against a spread of an untyped object.
 */
const ENFORCED = [
  'aria-hidden',
  'focusable',
  'fill',
  'stroke',
  'strokeWidth',
  'stroke-width',
  'viewBox',
  'children',
] as const

export function Icon({
  name,
  size = 'md',
  className,
  ...props
}: Omit<SVGProps<SVGSVGElement>, 'name' | (typeof ENFORCED)[number]> & {
  name: IconName
  size?: IconSize
}) {
  const classes = [SIZE_CLASS[size]]
  if (DIRECTIONAL.has(name)) {
    classes.push('icon--flip')
  }
  if (className !== undefined) {
    classes.push(className)
  }
  const passthrough: Record<string, unknown> = { ...props }
  for (const key of ENFORCED) {
    delete passthrough[key]
  }
  return (
    <svg
      className={classes.join(' ')}
      {...passthrough}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      {ICON_PATHS[name].map((path) => (
        <path key={path} d={path} />
      ))}
    </svg>
  )
}
